import {
  getChildCount,
  hasNoChildren,
  hasNoParents,
  isEmptyFamily,
  matchesFamilySearch,
  type FamilyListRecord,
} from "../familyListUtils";
import { calculateAge } from "../../utils/dateUtils";

export type FamilyReportRow = FamilyListRecord & {
  id: string | number;
  active?: boolean;
  familySituation?: string;
};

export type FamilyStatusFilter =
  | "all"
  | "active"
  | "inactive"
  | "formerSchoolChildren"
  | "multipleChildren"
  | "noChildren"
  | "noParents"
  | "empty";

type ChildAgeLabels = {
  age: (value: number) => string;
  unknownAge: string;
};

export const formatFamilyChildren = (
  family: FamilyReportRow,
  labels: ChildAgeLabels,
  asOf = new Date(),
): string => {
  if (!Array.isArray(family.children) || family.children.length === 0) return "—";

  return family.children
    .map((child) => {
      const name = [child.firstName, child.lastName].filter(Boolean).join(" ").trim() || "—";
      const age = calculateAge(child.dateBirth, asOf);
      return `${name} (${typeof age === "number" ? labels.age(age) : labels.unknownAge})`;
    })
    .join(", ");
};

export const filterFamilyReportRows = (
  rows: FamilyReportRow[],
  searchQuery: string,
  status: FamilyStatusFilter,
  sortEmptyFirst: boolean,
): FamilyReportRow[] =>
  rows
    .filter((family) => {
      if (!matchesFamilySearch(family, searchQuery)) return false;
      if (status === "active") return family.active === true;
      if (status === "inactive") return family.active !== true;
      if (status === "formerSchoolChildren") {
        return getChildCount(family) > 0 && family.active !== true;
      }
      if (status === "multipleChildren") return getChildCount(family) > 1;
      if (status === "noChildren") return hasNoChildren(family);
      if (status === "noParents") return hasNoParents(family);
      if (status === "empty") return isEmptyFamily(family);
      return true;
    })
    .sort((a, b) => {
      if (sortEmptyFirst && isEmptyFamily(a) !== isEmptyFamily(b)) {
        return isEmptyFamily(a) ? -1 : 1;
      }
      return (a.familyName || "").localeCompare(b.familyName || "", "nl");
    });

export const buildFamilyClipboardText = (
  rows: FamilyReportRow[],
  columnKeys: string[],
  headers: Record<string, string>,
  values: Record<string, (family: FamilyReportRow) => string>,
): string =>
  [
    columnKeys.map((key) => sanitizeSpreadsheetCell(headers[key] || key)).join("\t"),
    ...rows.map((family) =>
      columnKeys
        .map((key) => sanitizeSpreadsheetCell(values[key]?.(family) ?? ""))
        .join("\t"),
    ),
  ].join("\n");

const sanitizeSpreadsheetCell = (value: string): string => {
  const normalized = String(value).replace(/[\t\r\n]+/g, " ");
  return /^\s*[=+\-@]/.test(normalized) ? `'${normalized}` : normalized;
};
